import { memo, useState, useRef, useMemo, useCallback, ReactElement, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Crud,
  CrudFormComponent,
  CrudMode,
  CrudProps,
  notification,
  rejectionError,
  ValidationError,
  transformCrudFormErrors,
} from '@ohpen/enso-ui';
import {
  ChangeRequestOfInterestRateRequest,
  InterestRateRequest,
  InterestRateCrudChangeResponse,
  interestRatesCreateChangeRequest,
} from '@pmp/api';
import { crudToActionMap, changeRequestNotificationMessage, changeRequestButtonMessage } from '@pmp/utils';
import { CrudFormProps } from '@pmp/types';
import { useFeatureToggle } from '@pmp/hooks';

import { getInterestRatesFormSchema } from '../formConfig';

export const InterestRateCrudForm = memo(({ mode, record, refreshTable, onClose }: CrudFormProps<InterestRateRequest>): ReactElement => {
  const ref = useRef<CrudFormComponent>(null);
  const [open, setOpen] = useState(true);
  const { t } = useTranslation();
  const enableFourEyes = useFeatureToggle(['enableFourEyes']);

  useEffect(() => {
    const { baseRateCode } = record || {};
    if (!baseRateCode) {
      notification().error({ message: t('global.errors.unexpected') });
      onClose();
    }
  }, [record, t, onClose]);

  const onConfirm: (formData: InterestRateRequest) => Promise<ValidationError[] | void> = useCallback(
    data => {
      if (!mode || !crudToActionMap[mode]) return Promise.reject(rejectionError('mode is required'));

      const dataToSend: InterestRateRequest = {
        ...data,
        baseRateCode: record?.baseRateCode || '',
      };

      const body: ChangeRequestOfInterestRateRequest = {
        data: dataToSend,
        actionType: crudToActionMap[mode],
      };
      return interestRatesCreateChangeRequest({ body }).then(({ data: { isValid, errors } }: { data: InterestRateCrudChangeResponse }) => {
        if (!isValid) {
          const form = ref.current?.form;
          if (form) {
            const errorFields = form.getFieldsError().map(f => ({ name: f.name, errors: [] }));
            if (errorFields.length) form.setFields(errorFields);
          }
          const allErrors = transformCrudFormErrors({ ...errors });
          return allErrors;
        }
        refreshTable();
        notification().success({
          message: changeRequestNotificationMessage(t, enableFourEyes),
        });
      });
    },
    [mode, record?.baseRateCode, refreshTable, t, enableFourEyes],
  );

  const handleOnClose = useCallback(() => {
    setOpen(false);
    setTimeout(() => {
      onClose();
    }, 300);
  }, [onClose]);

  const title = useMemo(() => {
    if (!mode) return '';
    const titles: { [key: string]: string } = {
      [CrudMode.CREATE]: `Create new interest rate for ${record?.baseRateCode}`,
      [CrudMode.EDIT]: `Edit ${record?.baseRateCode} rate`,
    };
    return titles[mode];
  }, [record?.baseRateCode, mode]);

  const crudProps: CrudProps<InterestRateRequest> = useMemo(() => {
    return {
      ref,
      open,
      schema: getInterestRatesFormSchema(mode),
      title,
      labels: {
        confirm: mode === CrudMode.EDIT ? t('global.labels.save') : changeRequestButtonMessage(t, enableFourEyes),
      },
      initialValues: record,
      mode: mode,
      onClose: handleOnClose,
      onConfirm,
    };
  }, [onConfirm, record, mode, open, title, handleOnClose, t, enableFourEyes]);

  return <Crud {...crudProps} />;
});
